import { getCompanyOptionWithPriceSelect } from '@/api/get/config/get-company-options-with-price-select';
import { getCompanyPriceListSelect } from '@/api/get/config/get-company-price-list-select';
import {
  PrimaryButton,
  PrimaryOutlineButton,
  TextButton,
} from '@/components/button/button-component';
import Card from '@/components/card/card-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import ProductCategories from '@/components/product/product-categories/product-categories';
import ProductDetailsForm from '@/components/product/product-details-form/product-details-form';
import LinkInput from '@/components/product/product-links/product-links';
import ProductOptionsComponent from '@/components/product/product-option/product-options-component';
import FileUploader from '@/components/react-hook-form-inputs/file-dropzone/file-dropzone-component';
import ImageGallery from '@/components/react-hook-form-inputs/image-gallery/image-gallery-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import ToggleSwitch from '@/components/react-hook-form-inputs/toggle-switch/toggle-switch-component';
import { Heading3Text } from '@/utils/global-fonts';
import { requiredValidationDropdownMessage } from '@/utils/validations';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { BsCheck2 } from 'react-icons/bs';
import { FiEye, FiX } from 'react-icons/fi';
import { MdLinkOff } from 'react-icons/md';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FieldWithRevert from '@/components/react-hook-form-inputs/field-with-revert/field-with-revert-component';
import Alert from '@/components/alert/alert-component';
import { FieldArrayRegistryProvider } from '@/components/product/product-option/context-provider';
import DocumentsUpload from '../../../components/product/product-documents/product-document-upload';
import { PRODUCT_CONTEXT } from './context-provider';
import Styled from './product-form-style';
import { recursiveFlatMap } from './utils';

/**
 * @param {boolean} isLoading if the item is loading
 * @returns {JSX.Element}
 */
const ProductForm = ({ isLoading, onApply }) => {
  const navigate = useNavigate();
  const { id, productId } = useParams();
  const location = useLocation();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { setPrices, setOptions, templateData } = useContext(PRODUCT_CONTEXT);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    getCompanyPriceListSelect(id).then((response) => {
      setPrices(response.data);
      setLoadingPrices(false);
    });
    getCompanyOptionWithPriceSelect(id).then((response) => {
      setOptions(response.data);
      setLoadingOptions(false);
    });
  }, [id, setOptions, setPrices]);

  const {
    watch,
    getValues,
    reset,
    setValue,
    formState: { errors, isLoading: formLoading, isDirty, dirtyFields },
  } = useFormContext();
  const optionStatus = watch('product.status');
  const templateId = watch('product.template_id');

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'invalid', label: 'Inactive' },
    { value: 'draft', label: 'Draft' },
  ];

  const clearProductData = () => {
    localStorage.setItem('productFormValues', JSON.stringify({}));
  };

  const handleCancel = () => {
    if (productId && isDirty && Object.keys(dirtyFields).length > 0) {
      ConfirmationModal({
        title: 'Confirm',
        text: `All unsaved changes will be lost. Are you sure you want to proceed?`,
        onConfirm: () => {
          clearProductData();
          navigate(-1);
        },
      });
    } else {
      clearProductData();
      navigate(-1);
    }
  };

  const refetchProduct = (updatedProductData) => {
    reset(updatedProductData);
    setLoadingSubmit(false);
  };

  // Helper function to update overrides array
  const updateOverrides = useCallback(
    (fieldName, shouldAdd) => {
      const currentValues = getValues(`product.overrides.${fieldName}`); // True | False | Undefined

      if (shouldAdd && !currentValues) {
        // We need to check if the field changed from the original value
        // This is done this way to access nested properties correctly, to avoid the undefined value error
        setValue(`product.overrides.${fieldName}`, true);
      } else if (!shouldAdd && currentValues) {
        setValue(`product.overrides.${fieldName}`, false);
      }
    },
    [getValues, setValue],
  );

  // Helper function to check if field value differs from original
  const isFieldOverridden = useCallback(
    (fieldName, currentValue) => {
      // Access nested properties correctly
      const keys = fieldName.split('.');
      const originalValue = keys.reduce((obj, key) => {
        if (obj && typeof obj === 'object') {
          return obj[key];
        }
        return undefined;
      }, templateData);

      if (Array.isArray(originalValue)) {
        if (currentValue.length === 0) {
          return currentValue !== originalValue;
        }
        return !currentValue.every((item) => originalValue.includes(item));
      }

      // For other fields, compare values
      return currentValue !== originalValue;
    },
    [templateData],
  );

  // Function to revert a field back to original value
  const handleRevert = useCallback(
    (fieldName, isProductOptions = false, optionData = null) => {
      // Access nested properties correctly

      const recursiveFindDataByPath = (template, keys) => {
        if (keys.length === 0) return template;
        const [key, ...rest] = keys;
        return recursiveFindDataByPath(template[key], rest);
      };

      const keys = fieldName.split('.');

      // The fieldName will be like this: productOptions.${}.option_price_link_id or productOptions.${}.dependencies
      // We need to grab the id and the field name at the end of the string.

      if (isProductOptions) {
        const { formName, templateOptionId, field } = optionData;
        const originalValue = recursiveFlatMap(
          templateData.productOptions,
          [],
        ).find(
          (option) => option.template_to_option_link_id === templateOptionId,
        );
        if (originalValue?.[field]) {
          if (field === 'option_price_link_id') {
            const nameField = formName.replace('option_price_link_id', 'name');
            setValue(nameField, originalValue.name);
          }
          setValue(formName, originalValue[field]);
          updateOverrides(fieldName, false);
        } else {
          Alert({
            icon: 'error',
            text: 'There was a problem reverting the field',
          });
        }

        return;
      }

      if (fieldName === 'productGallery') {
        // We need to compare the original value and only add / remove the ones that are not in the original value when adding the id needs to be duplicate
        const originalValue = recursiveFindDataByPath(templateData, keys);
        const newValue = getValues(fieldName);
        const toRemove = newValue.filter(
          (item) =>
            !originalValue.some(
              (originalItem) => originalItem.image === item.image,
            ),
        );
        const toAdd = originalValue.filter(
          (item) =>
            newValue.file ||
            !newValue.some((newValueItem) => newValueItem.image === item.image),
        );
        const newGallery = newValue.filter((item) => {
          if (toRemove.find((toRemoveItem) => toRemoveItem.id === item.id)) {
            return false;
          }
          return true;
        });

        const addGallery = toAdd.map((item) => ({
          ...item,
          id: 'duplicate',
        }));

        setValue(fieldName, [...newGallery, ...addGallery]);
        updateOverrides(fieldName, false);
        return;
      }

      const originalValue = recursiveFindDataByPath(templateData, keys);

      // Revert regular field
      setValue(fieldName, originalValue);

      // Remove from overrides
      updateOverrides(fieldName, false);
    },
    [templateData, setValue, updateOverrides, getValues],
  );

  // Enhanced blur handler that checks for overrides and updates override state
  const createBlurHandler = useCallback(
    (fieldName) => (event) => {
      const newValue = event.target ? event.target.value : event;

      const isOverridden = isFieldOverridden(fieldName, newValue);
      updateOverrides(fieldName, isOverridden);
    },
    [isFieldOverridden, updateOverrides],
  );

  // Enhanced rich text blur handler
  const createRichTextBlurHandler = useCallback(
    (fieldName) => (value) => {
      // Check if field is overridden and update overrides array
      const isOverridden = isFieldOverridden(fieldName, value);
      updateOverrides(fieldName, isOverridden);
    },
    [isFieldOverridden, updateOverrides],
  );

  // Enhanced file change handler
  const createFileChangeHandler = useCallback(
    (fieldName) => (files) => {
      // Set the form value first
      setValue(fieldName, files, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Check if field is overridden and update overrides array
      const isOverridden = isFieldOverridden(fieldName, files);
      updateOverrides(fieldName, isOverridden);
    },
    [isFieldOverridden, updateOverrides, setValue],
  );

  // Single select handler
  const handleSingleSelectChange = (fieldName, selectedOption) => {
    if (selectedOption) {
      setValue(fieldName, selectedOption?.value);
    } else {
      setValue(fieldName, null);
    }

    // Check if field is overridden and update overrides array
    const isOverridden = isFieldOverridden(fieldName, selectedOption?.value);
    updateOverrides(fieldName, isOverridden);
  };

  // Multi select handler
  const handleMultiSelectChange = (fieldName, selectedOptions) => {
    // Check if field is overridden and update overrides array
    const isOverridden = isFieldOverridden(fieldName, selectedOptions);
    updateOverrides(fieldName, isOverridden);
  };

  return isLoading || formLoading || loadingPrices || loadingOptions ? (
    <Styled.Wrapper>
      <Loader loading />
    </Styled.Wrapper>
  ) : (
    <Styled.Wrapper>
      <Col xs={12} md={8} lg={8}>
        <Row xs={12} md={12} lg={12}>
          <Card padding="8px 5px 5px 12px">
            <Col>
              <Row>
                <Heading3Text>Product Information</Heading3Text>
              </Row>
              <Row>
                <ProductDetailsForm
                  CONTEXT={PRODUCT_CONTEXT}
                  handleRevert={handleRevert}
                  createBlurHandler={createBlurHandler}
                  handleSingleSelectChange={handleSingleSelectChange}
                  createRichTextBlurHandler={createRichTextBlurHandler}
                />
              </Row>
            </Col>
          </Card>
          <Card
            padding="8px 5px 5px 12px"
            margin="16px 0 0 "
            $minHeight="600px"
          >
            <Col id="options-card">
              <Row>
                <Heading3Text>Product Options</Heading3Text>
              </Row>
              <Row>
                <FieldArrayRegistryProvider>
                  <ProductOptionsComponent
                    name="productOptions"
                    CONTEXT={PRODUCT_CONTEXT}
                    handleRevert={handleRevert}
                  />
                </FieldArrayRegistryProvider>
              </Row>
            </Col>
          </Card>
        </Row>
      </Col>
      <Styled.SideCol xs={12} md={4} lg={4}>
        <Card
          margin="0px 24px 16px 0px"
          padding={productId ? '0px 0px' : '15px'}
        >
          <Styled.ActionsContainer noPadding={!productId}>
            <Styled.Header>Actions</Styled.Header>
            <SelectSearch
              cy="selCompanyStatus"
              header="Status"
              name="product.status"
              label="Please select a status"
              options={statusOptions}
              icon={<Styled.StatusCircle status={optionStatus} />}
              required={{
                required: requiredValidationDropdownMessage,
              }}
            />
            <Styled.ActionWrapper>
              <Styled.LeftActionsWrapper>
                <PrimaryOutlineButton
                  buttonWidth="95px"
                  type="Button"
                  loading={isLoading}
                  title="Cancel"
                  onClick={handleCancel}
                  icon={<FiX />}
                  margin="0 0 0 0"
                />
              </Styled.LeftActionsWrapper>
              <Styled.RightActionsWrapper>
                {productId && (
                  <PrimaryOutlineButton
                    buttonWidth="90px"
                    margin="0 8px 0 0"
                    type="Button"
                    loading={isLoading || loadingSubmit}
                    title="Apply"
                    onClick={() => {
                      if (!Object.keys(errors).length) {
                        const values = getValues();
                        onApply?.(values, (updatedProduct) => {
                          setLoadingSubmit(true);
                          refetchProduct(updatedProduct);
                        });
                      }
                    }}
                    icon={<BsCheck2 />}
                  />
                )}
                <PrimaryButton
                  buttonWidth={productId ? '116px' : '108px'}
                  type="submit"
                  loading={isLoading || loadingSubmit}
                  onClick={() => {
                    if (!errors) {
                      clearProductData();
                      setLoadingSubmit(!loadingSubmit);
                    }
                  }}
                  title={productId ? 'Save & Close' : 'Save'}
                />
              </Styled.RightActionsWrapper>
            </Styled.ActionWrapper>
          </Styled.ActionsContainer>
          {productId && (
            <Styled.ExtraActionsWrapper>
              <TextButton
                type="Button"
                loading={isLoading}
                title="Preview"
                onClick={() => {
                  const formValues = getValues();
                  localStorage.setItem(
                    'productFormValues',
                    JSON.stringify(formValues),
                  );

                  window.open(`${location.pathname}/preview`, '_blank');
                }}
                icon={<FiEye />}
                margin="0 0"
                padding="0 0"
              />
              {templateId && (
                <TextButton
                  type="Button"
                  loading={isLoading}
                  title="Detach"
                  onClick={() => {
                    setValue('product.template_id', null);
                  }}
                  icon={<MdLinkOff size={18} />}
                  margin="0 0"
                  padding="0 0"
                />
              )}
            </Styled.ExtraActionsWrapper>
          )}
        </Card>

        <Card padding="16px 18px 10px 18px" min-height="284px">
          <Styled.Header>Image Thumbnail</Styled.Header>
          <FieldWithRevert
            fieldName="product.thumbnail"
            tooltipMessage="Revert Thumbnail"
            handleRevert={handleRevert}
            right="5px"
            top="23px"
          >
            <FileUploader
              headerText="Upload Thumbnail"
              name="product.thumbnail"
              onChange={createFileChangeHandler('product.thumbnail')}
            />
          </FieldWithRevert>
        </Card>
        <Styled.CategoryWrapper>
          <Styled.CategoryContainer>
            <Styled.Header>
              Categories
              <FieldWithRevert
                fieldName="categories"
                tooltipMessage="Revert Categories"
                handleRevert={handleRevert}
                inlineRevert
              />
            </Styled.Header>
            <ProductCategories
              handleMultiSelectChange={handleMultiSelectChange}
            />
          </Styled.CategoryContainer>
        </Styled.CategoryWrapper>
        <Styled.GalleryWrapper>
          <Styled.UploadContainer $marginBottom="0px">
            <Card padding="8px 5px 5px 12px">
              <Styled.Header>Product Gallery</Styled.Header>
              <FieldWithRevert
                fieldName="productGallery"
                tooltipMessage="Revert Gallery"
                handleRevert={handleRevert}
                right="5px"
                top="-20px"
              >
                <ImageGallery
                  name="productGallery"
                  onChange={createFileChangeHandler('productGallery')}
                />
              </FieldWithRevert>
            </Card>
          </Styled.UploadContainer>
          <Styled.ActionArea
            bodyStyle={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <ToggleSwitch
              label="Override Unreal Engine Images"
              name="product.override_unreal_images"
              registerOptions={{}}
              onChange={createBlurHandler('product.override_unreal_images')}
            />
            <FieldWithRevert
              fieldName="product.override_unreal_images"
              tooltipMessage="Revert Status"
              inlineRevert
              handleRevert={handleRevert}
            />
          </Styled.ActionArea>
        </Styled.GalleryWrapper>
        <Card>
          <Styled.DocumentUploadWrapper>
            <Styled.Header>
              Upload Documents
              <FieldWithRevert
                fieldName="productDocuments"
                tooltipMessage="Revert Status"
                right="45px"
                top="-15px"
                translateY="-50%"
                handleRevert={handleRevert}
              />
            </Styled.Header>
            <DocumentsUpload
              name="productDocuments"
              handleMultiSelectChange={(fields) =>
                handleMultiSelectChange('productDocuments', fields)
              }
            />
          </Styled.DocumentUploadWrapper>
        </Card>
        <Card>
          <Styled.Header>
            Add Links
            <FieldWithRevert
              fieldName="productLinks"
              tooltipMessage="Revert Status"
              right="45px"
              top="-15px"
              translateY="-50%"
              handleRevert={handleRevert}
            />
          </Styled.Header>
          <LinkInput
            name="productLinks"
            handleMultiSelectChange={(fields) =>
              handleMultiSelectChange('productLinks', fields)
            }
          />
        </Card>
      </Styled.SideCol>
    </Styled.Wrapper>
  );
};

ProductForm.defaultProps = {
  isLoading: false,
  onApply: undefined,
};

ProductForm.propTypes = {
  isLoading: PropTypes.bool,
  onApply: PropTypes.func,
};

export default ProductForm;
